import { FC, useState, useEffect } from "react";
import {
  Box,
  List,
  Paper,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSearchParams } from "react-router-dom";
import { EmailListItem } from "../../internal/Email/EmailListItemComponent";
import { InternalEmailThreadComponent } from "../../internal/Email/InternalEmailThreadComponent";
import { EmailThread } from "@services/documents/email/types";

export const QuoteEmailsInternalPage: FC = () => {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [selectedThread, setSelectedThread] =
    useState<EmailThread | null>(null);
  const [relatedThreads, setRelatedThreads] = useState<
    EmailThread[]
  >([]);
  const [selectedSecondaryThread, setSelectedSecondaryEmail] =
    useState<EmailThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingRelatedThreads, setLoadingRelatedThreads] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    const fetchQuoteThreads = async () => {
      try {
        setError(null);
        const response =
          await apiClient.post<{ threads: EmailThread[] }>(
            "/documents/email/search_in_stored_threads",
            { tags: ["contains_quote"] }
          );
        setThreads(response.threads);

        const mainThreadId = searchParams.get("mainThreadId");

        if (mainThreadId) {
          const mainThread = response.threads.find(
            (t) => t.threadId === mainThreadId
          );

          if (mainThread) {
            // Load main thread and related threads first
            await handleThreadSelect(mainThread, true);
          } else {
            // Cleanup invalid main ID
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("mainThreadId");
            newParams.delete("secondaryThreadId");
            setSearchParams(newParams);
          }
        }
      } catch (err) {
        setError("Failed to load threads");
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchQuoteThreads();
  }, []);

  useEffect(() => {
    if (!isInitialLoad) {
      const newParams = new URLSearchParams();
      if (selectedThread) {
        newParams.set("mainThreadId", selectedThread.threadId);
      }
      if (selectedSecondaryThread) {
        newParams.set("secondaryThreadId", selectedSecondaryThread.threadId);
      }
      setSearchParams(newParams);
    }
  }, [selectedThread, selectedSecondaryThread, isInitialLoad, setSearchParams]);

  const fetchRelatedThreads = async (threadId: string) => {
    try {
      setLoadingRelatedThreads(true);
      setRelatedThreads([]);
      const response =
        await apiClient.post<{ threads: EmailThread[] }>(
          "/documents/email/find_related_theads",
          { threadId }
        );
      setRelatedThreads(
        response.threads.filter((t) => t.threadId !== threadId)
      );
    } catch {
      setRelatedThreads([]);
    } finally {
      setLoadingRelatedThreads(false);
    }
  };

  const handleThreadSelect = async (
    thread: EmailThread,
    isInitialLoad = false
  ) => {
    setSelectedThread(thread);
    setSelectedSecondaryEmail(null);
    await fetchRelatedThreads(thread.threadId);

    if (!isInitialLoad) {
      const newParams = new URLSearchParams();
      newParams.set("mainThreadId", thread.threadId);
      setSearchParams(newParams);
    }
  };

  const handleEmailSelect = (thread: EmailThread) => {
    setSelectedSecondaryEmail(thread);
  };

  // Handle secondary thread selection after related threads load
  useEffect(() => {
    if (!loadingRelatedThreads && selectedThread) {
      const secondaryThreadId = searchParams.get("secondaryThreadId");
      if (secondaryThreadId) {
        const allThreads = [selectedThread, ...relatedThreads];
        const secondaryThread = allThreads.find(
          (t) => t.threadId === secondaryThreadId
        );

        if (secondaryThread) {
          setSelectedSecondaryEmail(secondaryThread);
        } else {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("secondaryThreadId");
          setSearchParams(newParams);
        }
      }
    }
  }, [
    loadingRelatedThreads,
    selectedThread,
    relatedThreads,
    searchParams,
    setSearchParams,
  ]);

  if (loading) {
    return (
      <AdminPage>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (error) {
    return <AdminPage>{error}</AdminPage>;
  }

  // Rest of the component remains the same with updated prop names
  return (
    <AdminPage
      containerStyle={{
        height: "calc(100vh - 100px)",
        pt: "20px",
        marginTop: 0,
        boxSizing: "content-box",
      }}
    >
      <Box display="flex" height="100%">
        {/* Left Sidebar - Quote Emails */}
        <Paper
          sx={{
            width: 320,
            height: "100%",
            overflowY: "auto",
            borderRight: 1,
            boxSizing: "border-box",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
          >
            Quote Emails
          </Typography>
          <List>
            {threads.map((thread) => (
              <Box key={thread.threadId}>
                <EmailListItem
                  id={thread.threadId}
                  sender=""
                  subject={thread.subject}
                  date={thread.lastDate.toString()}
                  additionalData={thread.metadata?.quote_data?.quotes || []}
                  isSelected={selectedThread?.threadId === thread.threadId}
                  onSelect={() => handleThreadSelect(thread)}
                />
                <Divider />
              </Box>
            ))}
          </List>
        </Paper>

        {/* Middle Column - Selected Thread and Related Threads */}
        <Paper
          sx={{
            width: 320,
            height: "100%",
            overflowY: "auto",
            borderRight: 1,
            borderColor: "divider",
          }}
        >
          {selectedThread ? (
            <>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
              >
                Selected Thread
              </Typography>
              <List>
                <EmailListItem
                  subject={selectedThread.subject}
                  date={selectedThread.lastDate.toString()}
                  sender=""
                  id={selectedThread.threadId}
                  isSelected={
                    selectedSecondaryThread?.threadId ===
                    selectedThread.threadId
                  }
                  onSelect={() => handleEmailSelect(selectedThread)}
                />
              </List>

              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
              >
                Related Threads
              </Typography>
              <List>
                {relatedThreads.map((thread) => (
                  <Box key={thread.threadId}>
                    <EmailListItem
                      subject={thread.subject}
                      sender=""
                      date={thread.lastDate.toString()}
                      id={thread.threadId}
                      tags={thread.tags}
                      additionalData={thread.metadata?.rate_data?.rates || []}
                      isSelected={
                        selectedSecondaryThread?.threadId === thread.threadId
                      }
                      onSelect={() => handleEmailSelect(thread)}
                    />
                    <Divider />
                  </Box>
                ))}

                {relatedThreads.length === 0 && !loadingRelatedThreads && (
                  <Typography sx={{ pl: 2 }}>No related Threads</Typography>
                )}

                {/* Loading related Threads */}
                {loadingRelatedThreads && (
                  <Box sx={{ pl: 2, pt: 1 }}>
                    <CircularProgress />
                  </Box>
                )}
              </List>
            </>
          ) : (
            <Typography color="text.secondary" p={2}>
              Select an email thread to see related threads
            </Typography>
          )}
        </Paper>

        {/* Right Column - Full Email Thread */}
        <Box flex={1} height="100%" sx={{ overflowY: "auto" }} p={2}>
          {selectedSecondaryThread ? (
            <InternalEmailThreadComponent
              threadId={selectedSecondaryThread.threadId}
            />
          ) : (
            <Typography color="text.secondary">
              Select an email to view details
            </Typography>
          )}
        </Box>
      </Box>
    </AdminPage>
  );
};
